/**
 * Org standards and design tokens (ADR 0050, R-PL-5): a layered, versioned library of guidance,
 * rules and design tokens. A standard is the existing DisciplinePack with guidance, a design-rules
 * file and a token set; layers go Orgus defaults, org, workspace, project, then the repo's own
 * files. The effective standards are delivered into every agent session at start (a budgeted,
 * relevant digest), enforced while the agent edits, at landing and in reports. Nothing is ever
 * written into a watched checkout: repo files go out as a pull request.
 *
 * This file is the contract. The compiler (`effectiveStandards` in packages/discipline), the
 * session delivery, the checks and the design module come with their packages. Browser-safe.
 */
import type { Id, Ts } from '../index.ts';

// ---------------------------------------------------------------------------
// Guidance
// ---------------------------------------------------------------------------

export type StandardsArea = 'coding' | 'testing' | 'security' | 'docs' | 'git-release' | 'design' | 'accessibility';
export const STANDARDS_AREAS: readonly StandardsArea[] = ['coding', 'testing', 'security', 'docs', 'git-release', 'design', 'accessibility'];
export const STANDARDS_AREA_LABEL: Record<StandardsArea, string> = {
  coding: 'Coding',
  testing: 'Testing',
  security: 'Security',
  docs: 'Documentation',
  'git-release': 'Git and releases',
  design: 'Design system',
  accessibility: 'Accessibility',
};

/** 'must' sections go into the digest first, then 'should'; 'tip' only when there is room. */
export type GuidancePriority = 'must' | 'should' | 'tip';
export const GUIDANCE_PRIORITY_ORDER: readonly GuidancePriority[] = ['must', 'should', 'tip'];

/** What agents and people read. Short Markdown; the checks in `ruleIds` enforce it. */
export interface GuidanceSection {
  /** 'react-components'. Sections merge by id across layers, narrowest wins. */
  id: string;
  title: string;
  /** repoHas: repo-relative paths (files or folders); the section fits only a repo that has one of them (a repo convention, such as .changeset). */
  appliesTo: { globs?: string[]; languages?: string[]; frameworks?: string[]; agentKinds?: string[]; repoHas?: string[] };
  text: string;
  priority: GuidancePriority;
  /** The checks that enforce it, so a violation can quote the guidance. */
  ruleIds?: string[];
  /** Set at org or workspace layer: a narrower layer cannot drop or override it. */
  locked?: boolean;
}

// ---------------------------------------------------------------------------
// Layers and the effective standards
// ---------------------------------------------------------------------------

/** Widest first; for settings the narrowest wins, and an enforced pin is a floor narrower layers only tighten. */
export type StandardsLayerKind = 'orgus' | 'org' | 'workspace' | 'project' | 'repo';
export const STANDARDS_LAYER_ORDER: readonly StandardsLayerKind[] = ['orgus', 'org', 'workspace', 'project', 'repo'];

/** One layer's contribution: pinned packs, guidance overrides and settings overrides. */
export interface StandardsLayer {
  kind: StandardsLayerKind;
  /** The workspace or project id; absent for 'orgus' and 'org'. */
  id?: Id;
  /** 'acme/web@1.2.0', 'orgus/typescript-react@1.0.0'. */
  packs: string[];
  guidance: GuidanceSection[];
  /** Section ids this layer drops (refused for locked sections). */
  dropGuidance?: string[];
  settings: Record<string, Record<string, unknown>>;
  /**
   * false: sessions in this layer's scope get no digest at start (the checks still run). The
   * narrowest layer that sets it wins; unset everywhere means on.
   */
  deliver?: boolean;
  updatedBy: string;
  updatedAt: Ts;
}

/** The default digest budget, under the 10,000 characters Claude Code allows a hook string. */
export const STANDARDS_DIGEST_BUDGET = 6000;
export const STANDARDS_HOOK_MAX_CHARS = 10_000;
/** The line every digest ends with. */
export const STANDARDS_DIGEST_FOOTER = 'Run `orgus check --changed` before you finish.';
/** The footer when `orgus check` cannot run for the session (no orgus CLI, no repo rulebook). */
export const STANDARDS_DIGEST_FOOTER_NO_CLI = 'Check your changes against them before you finish.';
/**
 * The line a session gets when its project has a landing gate (an Orgus lander, flow.land or a
 * pipeline in orgus.yml, or a land script): the queue runs the full gate, so a session that runs
 * it too only doubles the work (a 15 to 25 minute verify before every submission, 29 Oct 2026).
 */
export const LANDING_GATE_LINE =
  'Run only the tests your change touches and the type check; the landing queue runs the full gate before main moves, so do not run it yourself. A repo instruction that the full gate must pass is satisfied by the landing.';
/** The footer with a landing gate: `orgus check --changed` reads only the changed files, so it stays. */
export const STANDARDS_DIGEST_FOOTER_GATED = `${LANDING_GATE_LINE} Run \`orgus check --changed\` before you finish: it checks only your changed files, in seconds.`;
export const STANDARDS_DIGEST_FOOTER_GATED_NO_CLI = `${LANDING_GATE_LINE} Check your changes against these standards before you finish.`;

/**
 * Does the project have a landing gate, a queue that verifies work before main moves? Yes when its
 * orgus.yml declares flow.land or a pipeline (the landing queue it shows), or package.json has a
 * `land` script. Absent or unreadable inputs count as no.
 */
export function hasLandingGate(f: { manifest?: unknown; packageScripts?: unknown }): boolean {
  const obj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
  const m = f.manifest;
  if (obj(m)) {
    if (obj(m.flow) && typeof m.flow.land === 'string' && m.flow.land.trim()) return true;
    if (obj(m.pipeline)) return true;
  }
  const s = f.packageScripts;
  return obj(s) && ['land', 'land:submit'].some((k) => typeof s[k] === 'string' && (s[k] as string).trim() !== '');
}

/**
 * What a checkout has that decides which standards its sessions get (QA-33), reported by the
 * machine so the hub preview matches the session digest.
 */
export interface StandardsRepoFacts {
  /** Each repoHas path the machine probed, and whether the checkout has it. */
  paths: Record<string, boolean>;
  /** Can `orgus check --changed` run for a session there? */
  checkCommand: boolean;
  /** Does the checkout have a landing gate? */
  landingGate: boolean;
}

/** What `effectiveStandards` reads besides the layers. */
export interface EffectiveStandardsOptions {
  /** Narrow guidance by appliesTo.globs to these files, when given. */
  files?: string[];
  agentKind: string;
  /** From the stack profile, to pick relevant sections. */
  languages?: string[];
  frameworks?: string[];
  budgetChars?: number;
  /** Does the repo have this repo-relative path? For appliesTo.repoHas. Absent: unknown, nothing is filtered by it. */
  repoHas?: (relPath: string) => boolean;
  /** Can `orgus check --changed` run for the session? False: the footer points to the Discipline page only. Default true. */
  checkCommand?: boolean;
  /** Does the project have a landing gate (hasLandingGate)? True: the footer says not to run the full gate locally. Default false. */
  landingGate?: boolean;
}

/** The compiled standards for one project: what a session gets and what checks run. */
export interface EffectiveStandards {
  projectId: Id;
  /** The budgeted, relevant text: must, then should, ending with the footer and where the full text is. */
  digest: string;
  /** The merged rulebook, the same YAML as .orgus/discipline.yaml, kept outside the repo. */
  rulebookText: string;
  /** The design-rules JSON text, generated or written for the org, when one applies. */
  designRules: string | null;
  tokensRef: { setId: Id; version: string } | null;
  /** Section ids in the digest, in order, and those left out for the budget. */
  included: string[];
  omitted: string[];
  /** False when a layer turned delivery off: the digest is still compiled, but no session gets it. */
  deliver: boolean;
  /** Packs or settings the layers name that could not be applied, each a sentence. */
  problems: string[];
  /** Changes whenever anything above changes: the re-delivery and repo-files trigger. */
  hash: string;
}

/** How each agent receives the digest at session start (ADR 0050 point 2). */
export type StandardsDelivery =
  | 'claude-session-start'
  | 'codex-developer-instructions'
  | 'acp-first-prompt'
  | 'copilot-instructions-dir'
  | 'cloud-preamble'
  | 'none';

/** GET /api/standards/effective */
export interface EffectiveStandardsResponse extends EffectiveStandards {
  delivery: StandardsDelivery;
  layers: { kind: StandardsLayerKind; id?: Id; packs: string[] }[];
}

/** GET /api/standards/layers */
export interface StandardsLayersState {
  layers: StandardsLayer[];
}

/** PUT /api/standards/layers: one layer's packs, guidance and settings. */
export type StandardsLayerUpdate = Omit<StandardsLayer, 'updatedBy' | 'updatedAt'>;

/**
 * Repo files Orgus offers as a pull request (never written into a checkout). Every path is
 * relative to the repo root with forward slashes, never absolute, never with a '..' or '.git'
 * segment, so a generated file set cannot reach outside the repo or into its git directory.
 */
export interface StandardsRepoFiles {
  projectId: Id;
  /** AGENTS.md (under 32 KiB), CLAUDE.md with @AGENTS.md, .orgus/discipline.yaml, design rules, token exports. */
  files: { path: string; text: string }[];
  /** The effective standards hash they came from. */
  hash: string;
}

/** Codex reads at most this much of AGENTS.md. */
export const AGENTS_MD_MAX_BYTES = 32 * 1024;

// ---------------------------------------------------------------------------
// Design tokens (W3C Design Tokens, DTCG 2025.10)
// ---------------------------------------------------------------------------

export const DTCG_VERSION = '2025.10';
export const DTCG_MEDIA_TYPE = 'application/design-tokens+json';

export type DtcgTokenType =
  | 'color'
  | 'dimension'
  | 'fontFamily'
  | 'fontWeight'
  | 'duration'
  | 'cubicBezier'
  | 'number'
  | 'strokeStyle'
  | 'border'
  | 'transition'
  | 'shadow'
  | 'gradient'
  | 'typography';

export const DTCG_TOKEN_TYPES: readonly DtcgTokenType[] = ['color', 'dimension', 'fontFamily', 'fontWeight', 'duration', 'cubicBezier', 'number', 'strokeStyle', 'border', 'transition', 'shadow', 'gradient', 'typography'];

/** Where a token set came from. */
export type TokenImportSource = 'dtcg-file' | 'tokens-studio' | 'figma-file' | 'figma-api' | 'orgus';

export interface TokenImportSourceInfo {
  id: TokenImportSource;
  label: string;
  /** The default import for every org is files; the Figma Variables API needs the customer's Figma Enterprise plan. */
  default: boolean;
  requires?: 'figma-enterprise';
  note: string;
}

/** Decision of 17 Oct 2026: Tokens Studio JSON and W3C token files for everyone; Figma's API only on Figma Enterprise. */
export const TOKEN_IMPORT_SOURCES: readonly TokenImportSourceInfo[] = [
  { id: 'dtcg-file', label: 'W3C design token files', default: true, note: 'Upload .tokens.json files in the DTCG 2025.10 format.' },
  { id: 'tokens-studio', label: 'Tokens Studio', default: true, note: 'Read the DTCG files Tokens Studio syncs to your repo, or upload them.' },
  { id: 'figma-file', label: 'Figma export file', default: true, note: "Upload the file Figma's own design token export writes." },
  {
    id: 'figma-api',
    label: 'Figma variables',
    default: false,
    requires: 'figma-enterprise',
    note: "Figma's Variables API is only on Figma Enterprise plans. On other Figma plans, export the file instead.",
  },
  { id: 'orgus', label: 'Orgus example set', default: false, note: "Orgus's own tokens, as a reference to start from." },
];

export type TokenExportFormat = 'css' | 'tailwind-v4' | 'js' | 'ts' | 'ios-swift' | 'android' | 'compose' | 'flutter';
export const TOKEN_EXPORT_FORMATS: readonly TokenExportFormat[] = ['css', 'tailwind-v4', 'js', 'ts', 'ios-swift', 'android', 'compose', 'flutter'];
export const TOKEN_EXPORT_FORMAT_LABEL: Record<TokenExportFormat, string> = {
  css: 'CSS variables',
  'tailwind-v4': 'Tailwind v4 theme',
  js: 'JavaScript',
  ts: 'TypeScript',
  'ios-swift': 'iOS Swift',
  android: 'Android resources',
  compose: 'Jetpack Compose',
  flutter: 'Flutter',
};

/** An org's design tokens, versioned like packs: a published version never changes. */
export interface TokenSet {
  /** tks_<ulid> */
  id: Id;
  name: string;
  version: string;
  scope: { kind: 'org' } | { kind: 'workspace'; id: Id };
  /** DTCG files: path and JSON text, unknown $extensions kept. */
  files: { path: string; json: string }[];
  /** The DTCG Resolver document (sets, modifiers such as light and dark), JSON text. */
  resolver?: string;
  source: TokenImportSource;
  /** Set once published. */
  publishedBy?: string;
  publishedAt?: Ts;
}

/** A finding on a token set itself: invalid, circular alias, low contrast, naming. */
export interface TokenSetFinding {
  rule: 'dtcg/invalid' | 'dtcg/alias-missing' | 'dtcg/alias-circular' | 'contrast/aa' | 'naming';
  path: string;
  token?: string;
  message: string;
  severity: 'error' | 'warn';
}

/** POST /api/design/token-sets: import from files (the default) or a provider. */
export interface TokenSetImportRequest {
  name: string;
  scope?: TokenSet['scope'];
  source: TokenImportSource;
  /** Uploaded files (attachments module ids) or file texts. */
  files?: { path: string; json: string }[];
  /** For tokens-studio through a provider connection: the repo path. */
  repoPath?: string;
  projectId?: Id;
  /** For figma-api: the Figma file key (the OAuth consent is a handoff). */
  figmaFileKey?: string;
}

/** GET /api/design/token-sets */
export interface DesignState {
  tokenSets: TokenSet[];
}

/** GET /api/design/token-sets/:id/export?format= */
export interface TokenExport {
  format: TokenExportFormat;
  files: { path: string; text: string }[];
}

/** POST /api/design/token-sets/:id/check */
export interface TokenSetCheck {
  findings: TokenSetFinding[];
}

/** POST /api/design/rules: generate a design-rules file from a token set and a component allowlist. */
export interface DesignRulesRequest {
  tokenSetId: Id;
  /** Allowed component packages ('@acme/ui'): design.components_from. */
  componentsFrom: string[];
  /** Primitives the org wraps, refused when imported directly ('@radix-ui/*', 'lucide-react'). */
  wrapped?: string[];
  /** The smallest font size, in px. */
  fontFloorPx?: number;
}

export interface DesignRulesResult {
  /** The design-rules JSON text. */
  rules: string;
  ruleIds: string[];
}

// ---------------------------------------------------------------------------
// Enforcement while the agent edits, and provenance at landing (ADR 0113, R-FN-4)
// ---------------------------------------------------------------------------

/** How a machine-checkable rule is enforced: mandatory denies the edit, advisory warns, off skips. */
export type StandardsRuleClass = 'mandatory' | 'advisory' | 'off';
/** What landing does with a branch that fails provenance: refuse it, park it for a person, or let it land. */
export type StandardsProvenancePolicy = 'refuse' | 'review' | 'allow';

/**
 * The org's enforcement setting (GET/PUT /api/standards/enforcement). Rules are the design-rules
 * file's counted rules (regex and siblings) by id. Unlisted rules: level 'error' is mandatory,
 * 'warn' advisory, 'review' and 'runtime' are never checked at edit time.
 */
export interface StandardsEnforcement {
  /** enforce: mandatory violations deny the edit; warn: everything only warns; off: no edit-time check. */
  mode: 'enforce' | 'warn' | 'off';
  mandatory: string[];
  advisory: string[];
  off: string[];
  /** A branch whose Orgus sessions were checked against an older mandatory pack than the current one. */
  staleSessions: StandardsProvenancePolicy;
  /** A branch submitted by an agent session the hub holds no provenance for (not launched, not controlled). */
  unknownSessions: StandardsProvenancePolicy;
  updatedBy?: string;
  updatedAt?: Ts;
}

export const STANDARDS_ENFORCEMENT_DEFAULT: StandardsEnforcement = { mode: 'enforce', mandatory: [], advisory: [], off: [], staleSessions: 'refuse', unknownSessions: 'review' };
/** One edit-time check stays within this; a file it cannot check in time is let through and marked. */
export const STANDARDS_EDIT_BUDGET_MS = 150;
/** Files larger than this are not checked at edit time (marked skipped; the landing gate still checks). */
export const STANDARDS_EDIT_MAX_BYTES = 512 * 1024;

/** Agent to hub (discipline 'provenance'): which mandatory pack one session's edits were checked against. */
export interface StandardsProvenance {
  sessionId: Id;
  machineId?: Id;
  projectId: Id | null;
  checkoutId: Id | null;
  cwd: string;
  /** standardsPackVersion of the org's mandatory pack the guard enforced, at the session's latest edit. */
  packVersion: string;
  /** Hash of the digest the session got at start, when it got one. */
  digestHash?: string | null;
  launched: boolean;
  /** Edit-time checks: run, denied, warned, let through unchecked (hub down, over budget, too large). */
  checked: number;
  denied: number;
  warned: number;
  failOpen: number;
  firstAt: Ts;
  lastAt: Ts;
}

/** GET /api/standards/enforcement */
export interface StandardsEnforcementState {
  enforcement: StandardsEnforcement;
  /** The current mandatory pack version; sessions checked against another one are stale. */
  packVersion: string;
}

/** The landing's provenance verdict for one branch. */
export interface StandardsProvenanceVerdict {
  verdict: 'ok' | 'refuse' | 'review';
  what?: string;
  why?: string;
  next?: string;
}
